import React, {useState} from "react";
import {Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography} from "@mui/material";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {useTranslation} from "react-i18next";
import {RequestI18N} from "@/translations";

interface CancellationReasonDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
    title: string;
    message: string;
    confirmButtonText: string;
    cancelButtonText: string;
    reasonLabel?: string;
    reasonPlaceholder?: string;
}

export const CancellationReasonDialog: React.FC<CancellationReasonDialogProps> = ({
    open,
    onClose,
    onConfirm,
    title,
    message,
    confirmButtonText,
    cancelButtonText,
    reasonLabel,
    reasonPlaceholder
}) => {
    const { t } = useTranslation();
    const [reason, setReason] = useState("");
    const [error, setError] = useState<string | null>(null);

    // Default values if not provided
    const fieldLabel = reasonLabel || t(RequestI18N.details.field.cancellationReason);
    const fieldPlaceholder = reasonPlaceholder || t(RequestI18N.details.field.cancellationReasonPlaceholder);

    const handleConfirm = () => {
        if (!reason.trim()) {
            setError(t(RequestI18N.form.validation.cancellationReasonRequired));
            return;
        }
        if (reason.length > 1000) {
            setError(t(RequestI18N.form.validation.cancellationReasonMaxLength));
            return;
        }
        onConfirm(reason);
    };

    const handleClose = () => {
        setReason("");
        setError(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                    <Typography>{message}</Typography>

                    <Alert severity="info" sx={{ mt: 1 }}>
                        {t(RequestI18N.details.message.cancellationReasonInfo)}
                    </Alert>

                    <TextField
                        label={fieldLabel}
                        placeholder={fieldPlaceholder}
                        multiline
                        rows={4}
                        value={reason}
                        onChange={(e) => {
                            setReason(e.target.value);
                            setError(null);
                        }}
                        error={!!error}
                        helperText={error || `${reason.length}/1000`}
                        required
                        fullWidth
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <ButtonEx label={cancelButtonText} onClick={handleClose} color="inherit" />
                <ButtonEx label={confirmButtonText} onClick={handleConfirm} color="error" disabled={!reason.trim()} />
            </DialogActions>
        </Dialog>
    );
};
